import { useState } from "react";
import { X, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import axios from "axios";

const API_URL = "https://backend.aktcorp.com/relateddocuments"; // ✅ New URL

const EditPdf = ({ pdf, onClose, onUpdated }) => {
  const [title, setTitle] = useState(pdf?.title || "");
  const [category, setCategory] = useState(pdf?.category || "");
  const [language, setLanguage] = useState(pdf?.language || "");
  const [fileType] = useState(pdf?.fileType || "pdf");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const jwtToken = localStorage.getItem("authToken"); // ✅ JWT Token

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "*Title is required";
    if (!category) newErrors.category = "*Category is required";
    if (!language) newErrors.language = "*Language is required";
    if (!fileType) newErrors.fileType = "*File type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpdate = async () => {
    if (!validate()) return;

    setLoading(true);
    let fileContent = null;

    if (file) {
      const reader = new FileReader();
      fileContent = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(file);
      });
    }

    const payload = {
      title,
      category,
      language,
      fileType,
      fileContent: fileContent || undefined,
    };

    try {
      await axios.put(`${API_URL}/${pdf.id}`, payload, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      setSuccess(true);
      toast.success("Document updated successfully!");
      setLoading(false);

      // ✅ Optional: Notify parent to refresh list
      if (onUpdated) onUpdated();

      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update document");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-red-600">
          <X size={24} />
        </button>

        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-2xl font-bold text-center mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500"
        >
          Edit Document
        </motion.h2>

        {loading ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            <p className="text-blue-500 text-lg">Updating...</p>
          </div>
        ) : success ? (
          <div className="flex flex-col items-center space-y-4">
            <CheckCircle size={40} className="text-green-500" />
            <p className="text-green-500 text-lg">Document Updated!</p>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="text-gray-600 font-medium">PDF Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full p-2 border rounded-lg mt-1 ${errors.title ? "border-red-500" : ""}`}
            />
            {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}

            <label className="text-gray-600 font-medium">Language:</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`w-full p-2 border rounded-lg mt-1 ${errors.language ? "border-red-500" : ""}`}
            >
              <option value="">Select Language</option>
              <option value="English">English</option>
              <option value="Simplified Chinese">Simplified Chinese</option>
              <option value="Spanish">Spanish</option>
              <option value="Portuguese">Portuguese</option>
              <option value="French">French</option>
            </select>
            {errors.language && <p className="text-red-500 text-sm">{errors.language}</p>}

            <label className="text-gray-600 font-medium">Category:</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={`w-full p-2 border rounded-lg mt-1 ${errors.category ? "border-red-500" : ""}`}
            >
              <option value="">Select Category</option>
              <option value="Technical Data Sheet">Technical Data Sheet</option>
              <option value="Product Certifications">Product Certifications</option>
              <option value="Sell Sheets & Brochures">Sell Sheets & Brochures</option>
              <option value="Submittal Forms">Submittal Forms</option>
              <option value="Other">Other</option>
            </select>
            {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}

            <label className="text-gray-600 font-medium">Upload New PDF (Optional):</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="w-full p-2 border rounded-lg bg-gray-100"
            />

            <div className="flex justify-between mt-4">
              <button
                onClick={handleUpdate}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              >
                Update
              </button>
              <button
                onClick={onClose}
                className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditPdf;
